import React, { useState, useEffect, useContext } from 'react';
import { enhancedApi } from '../api';
import PageLayout from '../components/ui/PageLayout';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
  IconButton,
  Collapse,
  Avatar,
  Menu,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  LightbulbOutlined as LightbulbIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { EmailSuggestionsContext } from '../contexts/EmailSuggestionsContext';
import { AuthContext } from '../components/AuthContext';
import SmartComposeEditor from '../components/ui/SmartComposeEditor';

const replyEditorModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link'],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ],
};

const InboxPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [suggestionsPanelOpen, setSuggestionsPanelOpen] = useState(true);
  const [actionError, setActionError] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [viewMode, setViewMode] = useState('portal');
  const [threadFilter, setThreadFilter] = useState('all');
  const [emailThreads, setEmailThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadDetailLoading, setThreadDetailLoading] = useState(false);
  const [threadsError, setThreadsError] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignAnchorEl, setAssignAnchorEl] = useState(null);
  const [assignThreadId, setAssignThreadId] = useState(null);
  const [threadActioningId, setThreadActioningId] = useState(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const { suggestions, count, loading: suggestionsLoading, error: suggestionsError, refresh } = useContext(EmailSuggestionsContext) || {};

  const getInitials = (person) => {
    if (!person) return '?';
    const first = (person.first_name || '').trim().charAt(0);
    const last = (person.last_name || '').trim().charAt(0);
    if (first || last) return ((first || '') + (last || '')).toUpperCase();
    return (person.username || '?').slice(0, 2).toUpperCase();
  };

  const getStatusChip = (status) => {
    if (status === 'resolved') return { label: 'Resolved', color: 'success' };
    if (status === 'in_progress') return { label: 'In Progress', color: 'info' };
    return { label: 'Open', color: 'default' };
  };

  const fetchEmailThreads = async () => {
    try {
      setThreadsLoading(true);
      setThreadsError(null);
      const response = await enhancedApi.getEmailThreads(threadFilter);
      const data = response?.data;
      setEmailThreads(data?.threads || []);
    } catch (err) {
      setThreadsError(err?.response?.data?.error || 'Failed to load email threads');
      setEmailThreads([]);
      // error captured in state
    } finally {
      setThreadsLoading(false);
    }
  };

  const fetchEmailThreadDetail = async (threadId) => {
    if (!threadId) {
      setThreadDetail(null);
      return;
    }
    try {
      setThreadDetailLoading(true);
      const response = await enhancedApi.getEmailThread(threadId);
      const data = response?.data;
      setThreadDetail(data);
    } catch (err) {
      // error captured in state
      setThreadDetail(null);
    } finally {
      setThreadDetailLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await enhancedApi.getUsers();
      const data = response?.data;
      setUsers(Array.isArray(data) ? data : (data?.results || data?.users || []));
    } catch (err) {
      // error captured in state
      setUsers([]);
    }
  };

  useEffect(() => {
    if (viewMode === 'emailThreads') {
      fetchEmailThreads();
      fetchUsers();
    }
  }, [viewMode, threadFilter]);

  useEffect(() => {
    if (selectedThread) {
      fetchEmailThreadDetail(selectedThread.id);
    } else {
      setThreadDetail(null);
    }
  }, [selectedThread?.id]);

  useEffect(() => {
    if (emailThreads.length === 0) {
      setSelectedThread(null);
    } else if (!selectedThread || !emailThreads.some((t) => t.id === selectedThread.id)) {
      setSelectedThread(emailThreads[0]);
    }
  }, [emailThreads, selectedThread]);

  const handleAssignThread = (e, threadId) => {
    e.stopPropagation();
    setAssignAnchorEl(e.currentTarget);
    setAssignThreadId(threadId);
  };

  const handleAssignClose = () => {
    setAssignAnchorEl(null);
    setAssignThreadId(null);
  };

  const handleAssignSelect = async (userId) => {
    if (!assignThreadId) return;
    setThreadActioningId(assignThreadId);
    setThreadsError(null);
    try {
      await enhancedApi.assignEmailThread(assignThreadId, userId);
      await fetchEmailThreads();
      if (selectedThread?.id === assignThreadId) {
        await fetchEmailThreadDetail(assignThreadId);
      }
    } catch (err) {
      setThreadsError(err?.response?.data?.error || 'Failed to assign');
      // error captured in state
    } finally {
      setThreadActioningId(null);
      handleAssignClose();
    }
  };

  const handleMarkResolved = async (e, threadId) => {
    e.stopPropagation();
    setThreadActioningId(threadId);
    setThreadsError(null);
    try {
      await enhancedApi.updateEmailThreadStatus(threadId, 'resolved');
      await fetchEmailThreads();
      if (selectedThread?.id === threadId) {
        await fetchEmailThreadDetail(threadId);
      }
    } catch (err) {
      setThreadsError(err?.response?.data?.error || 'Failed to update status');
      // error captured in state
    } finally {
      setThreadActioningId(null);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await enhancedApi.getInboxClients();
      const data = response?.data;
      setClients(data?.data || []);
    } catch (err) {
      setError('Failed to load inbox');
      // error captured in state
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (client) => {
    if (!client?.client_id) {
      setConversation([]);
      return;
    }
    try {
      setConversationLoading(true);
      const response = await enhancedApi.getInboxClientConversation(client.client_id);
      const data = response?.data;
      const messages = data?.data || [];
      setConversation(messages);
      const lastFromClient = messages.filter((m) => m.is_from_client).pop();
      setReplySubject(lastFromClient ? `Re: ${lastFromClient.subject || 'Message'}` : 'Re: Message');
      // Refresh client list to update unread counts (backend marks messages read when viewing)
      const clientsRes = await enhancedApi.getInboxClients();
      const clientsData = clientsRes?.data;
      if (clientsData?.data) setClients(clientsData.data);
    } catch (err) {
      // error captured in state
      setConversation([]);
    } finally {
      setConversationLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if ((suggestions?.length ?? 0) > 0 && suggestionsPanelOpen === undefined) {
      setSuggestionsPanelOpen(true);
    }
  }, [suggestions?.length]);

  useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      setSelectedClient(clients[0]);
    }
  }, [clients, selectedClient]);

  useEffect(() => {
    if (selectedClient) {
      fetchConversation(selectedClient);
      setReplyContent('');
      setReplyError(null);
    } else {
      setConversation([]);
    }
  }, [selectedClient?.client_id]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString();
  };

  const isReplyEmpty = (html) => {
    if (!html) return true;
    const stripped = html.replace(/<[^>]*>/g, '').trim();
    return !stripped;
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!selectedClient || isReplyEmpty(replyContent)) return;
    setReplySending(true);
    setReplyError(null);
    try {
      await enhancedApi.replyToInboxClient(selectedClient.client_id, {
        subject: replySubject,
        message: replyContent
      });
      setReplyContent('');
      await fetchConversation(selectedClient);
      await fetchClients();
    } catch (err) {
      setReplyError(err?.response?.data?.error || 'Failed to send reply');
      // error captured in state
    } finally {
      setReplySending(false);
    }
  };

  const handleAcceptSuggestion = async (id) => {
    setActioningId(id);
    setActionError(null);
    try {
      await enhancedApi.acceptEmailSuggestion(id);
      await refresh?.();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Failed to create task');
      // error captured in state
    } finally {
      setActioningId(null);
    }
  };

  const handleDismissSuggestion = async (id) => {
    setActioningId(id);
    setActionError(null);
    try {
      await enhancedApi.dismissEmailSuggestion(id);
      await refresh?.();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Failed to dismiss');
      // error captured in state
    } finally {
      setActioningId(null);
    }
  };

  const getConfidenceLabel = (score) => {
    if (score >= 0.7) return { label: 'High', color: 'success' };
    if (score >= 0.3) return { label: 'Medium', color: 'warning' };
    return null;
  };

  return (
    <PageLayout sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
        <Paper elevation={0} sx={{ flexShrink: 0, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <InboxIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Inbox</Typography>
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'portal' ? 'Client conversations – select a client to view messages' : 'Email threads – assign, filter, and manage'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left panel: Portal Messages (clients) or Email Threads */}
          <Paper
            elevation={0}
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 0
            }}
          >
            {/* View toggle – inside left panel so always visible (not covered by Search) */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, v) => v != null && setViewMode(v)}
                size="small"
                fullWidth
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.5, fontSize: '0.8125rem' } }}
              >
                <ToggleButton value="portal">Portal Messages</ToggleButton>
                <ToggleButton value="emailThreads">Email Threads</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {viewMode === 'portal' ? (
              <>
                {loading ? (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 1 }} />
                    ))}
                  </Box>
                ) : error ? (
                  <Box sx={{ p: 2 }}>
                    <Alert severity="error">{error}</Alert>
                  </Box>
                ) : clients.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <EmailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">No client messages yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      When clients send messages through the portal, they will appear here.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {clients.map((c) => (
                      <Box
                        key={c.client_id}
                        onClick={() => setSelectedClient(c)}
                        sx={{
                          px: 2,
                          py: 2,
                          cursor: 'pointer',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          bgcolor: selectedClient?.client_id === c.client_id ? 'action.selected' : 'transparent',
                          borderLeft: '4px solid',
                          borderLeftColor: selectedClient?.client_id === c.client_id ? 'primary.main' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {c.client_name}
                              </Typography>
                              {c.unread_count > 0 && (
                                <Chip label={c.unread_count} size="small" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                              {c.last_subject || c.last_message_preview || 'No subject'}
                            </Typography>
                            {c.last_at && (
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                {formatTime(c.last_at)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ToggleButtonGroup
                    value={threadFilter}
                    exclusive
                    onChange={(e, v) => v != null && setThreadFilter(v)}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.5, fontSize: '0.8125rem' } }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="mine">My Emails</ToggleButton>
                    <ToggleButton value="unassigned">Unassigned</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {threadsError && (
                  <Alert severity="error" sx={{ mx: 2, mt: 1, py: 0 }} onClose={() => setThreadsError(null)}>
                    {threadsError}
                  </Alert>
                )}
                {threadsLoading ? (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: 1 }} />
                    ))}
                  </Box>
                ) : emailThreads.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <EmailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">No email threads</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {threadFilter === 'mine'
                        ? 'No threads assigned to you. Assign threads from All or Unassigned to see them here.'
                        : threadFilter === 'unassigned'
                          ? 'No unassigned threads'
                          : 'No threads yet'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {emailThreads.map((t) => {
                      const statusChip = getStatusChip(t.status);
                      const isActioning = threadActioningId === t.id;
                      return (
                        <Box
                          key={t.id}
                          onClick={() => setSelectedThread(t)}
                          sx={{
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: selectedThread?.id === t.id ? 'action.selected' : 'transparent',
                            borderLeft: '4px solid',
                            borderLeftColor: selectedThread?.id === t.id ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleAssignThread(e, t.id)}
                              disabled={isActioning}
                              sx={{ p: 0, flexShrink: 0 }}
                            >
                              <Avatar
                                sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.400' }}
                                src={t.assigned_to?.photo_url}
                              >
                                {getInitials(t.assigned_to)}
                              </Avatar>
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Chip label={statusChip.label} color={statusChip.color} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                {t.status !== 'resolved' && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleMarkResolved(e, t.id)}
                                    disabled={isActioning}
                                    title="Mark as resolved"
                                    sx={{ p: 0 }}
                                  >
                                    <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                                  </IconButton>
                                )}
                              </Box>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ display: 'block', mt: 0.25 }}>
                                {t.subject || 'No subject'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                                {t.client_name || 'Unknown client'}
                              </Typography>
                              {t.updated_at && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                  {formatTime(t.updated_at)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Paper>

          {/* Assign dropdown menu */}
          <Menu
            anchorEl={assignAnchorEl}
            open={Boolean(assignAnchorEl)}
            onClose={handleAssignClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {users.map((u) => (
              <MenuItem key={u.id} onClick={() => handleAssignSelect(u.id)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'grey.400' }}>
                    {getInitials(u)}
                  </Avatar>
                  <Typography variant="body2">
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || `User ${u.id}`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            {users.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">No staff members</Typography>
              </MenuItem>
            )}
          </Menu>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.default', minWidth: 0 }}>
            {viewMode === 'portal' && !selectedClient ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <PersonIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">Select a client to view the conversation</Typography>
                </Box>
              </Box>
            ) : viewMode === 'emailThreads' && !selectedThread ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <EmailIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">Select a thread to view communications</Typography>
                </Box>
              </Box>
            ) : viewMode === 'emailThreads' ? (
              <>
                <Paper elevation={0} sx={{ flexShrink: 0, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{threadDetail?.subject || selectedThread?.subject || 'No subject'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {threadDetail?.client_name || selectedThread?.client_name || 'Unknown client'}
                    </Typography>
                  </Box>
                  {threadDetail?.client_id && (
                    <Button
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(`/client/${threadDetail.client_id}`)}
                      sx={{ textTransform: 'none' }}
                    >
                      View client
                    </Button>
                  )}
                </Paper>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  {threadDetailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : !threadDetail?.communications?.length ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <EmailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary">No communications in this thread</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {threadDetail.communications.map((comm) => (
                        <Box
                          key={comm.id}
                          sx={{ display: 'flex', justifyContent: comm.direction === 'inbound' ? 'flex-start' : 'flex-end' }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: '85%',
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              bgcolor: comm.direction === 'inbound' ? 'background.paper' : 'primary.main',
                              color: comm.direction === 'inbound' ? 'text.primary' : 'primary.contrastText',
                              border: comm.direction === 'inbound' ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, opacity: 0.9 }}>
                              <Typography variant="caption" fontWeight={500}>{comm.created_by_name || 'Unknown'}</Typography>
                              <Typography variant="caption">{formatTime(comm.created_at)}</Typography>
                            </Box>
                            {comm.subject && (
                              <Typography variant="caption" fontWeight={500} sx={{ display: 'block', mb: 0.5 }}>{comm.subject}</Typography>
                            )}
                            {/<\w[^>]*>/i.test(comm.content || '') ? (
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{ '& p': { margin: '0 0 0.5em 0' }, '& p:last-child': { marginBottom: 0 } }}
                                dangerouslySetInnerHTML={{ __html: comm.content || '' }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comm.content}</Typography>
                            )}
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Paper elevation={0} sx={{ flexShrink: 0, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{selectedClient?.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">Conversation</Typography>
                  </Box>
                  <Button
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(`/client/${selectedClient.client_id}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    View client
                  </Button>
                </Paper>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  {conversationLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : conversation.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <EmailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary">No messages in this conversation</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {conversation.map((msg) => (
                        <Box
                          key={msg.id}
                          sx={{
                            display: 'flex',
                            justifyContent: msg.is_from_client ? 'flex-start' : 'flex-end'
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: '85%',
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              bgcolor: msg.is_from_client ? 'background.paper' : 'primary.main',
                              color: msg.is_from_client ? 'text.primary' : 'primary.contrastText',
                              border: msg.is_from_client ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, opacity: 0.9 }}>
                              <Typography variant="caption" fontWeight={500}>{msg.author}</Typography>
                              <Typography variant="caption">{formatTime(msg.created_at)}</Typography>
                            </Box>
                            {msg.subject && msg.subject !== 'Re: Message' && (
                              <Typography variant="caption" fontWeight={500} sx={{ display: 'block', mb: 0.5 }}>{msg.subject}</Typography>
                            )}
                            {/<\w[^>]*>/i.test(msg.message || '') ? (
                              <Typography
                                variant="body2"
                                component="div"
                                sx={{ '& p': { margin: '0 0 0.5em 0' }, '& p:last-child': { marginBottom: 0 } }}
                                dangerouslySetInnerHTML={{ __html: msg.message || '' }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.message}</Typography>
                            )}
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Paper elevation={0} sx={{ flexShrink: 0, p: 3, borderTop: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
                  <Box component="form" onSubmit={handleReply} sx={{ maxWidth: 720, mx: 'auto' }}>
                    <SmartComposeEditor
                      value={replyContent}
                      onChange={setReplyContent}
                      threadId={String(selectedThread?.id ?? '')}
                      clientId={String(threadDetail?.client_id ?? selectedThread?.client_id ?? '')}
                      placeholder="Type your reply..."
                      rows={6}
                    />
                    {replyError && <Alert severity="error" sx={{ mt: 2, py: 0 }}>{replyError}</Alert>}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={replySending || isReplyEmpty(replyContent)}
                      sx={{ mt: 2, textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      {replySending ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </Box>
                </Paper>
              </>
            )}
          </Box>

          {/* Suggested Tasks panel */}
          <Collapse in={suggestionsPanelOpen} orientation="horizontal" sx={{ flexShrink: 0 }}>
            <Paper
              elevation={0}
              sx={{
                width: 320,
                borderLeft: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 0
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightbulbIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Suggested Tasks</Typography>
                  {(count ?? 0) > 0 && (
                    <Chip label={count} size="small" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSuggestionsPanelOpen(false)}
                  aria-label="Collapse panel"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {suggestionsLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="caption" color="text.secondary">Loading suggestions...</Typography>
                  </Box>
                ) : suggestionsError ? (
                  <Box>
                    <Alert severity="error" sx={{ mb: 2 }}>{suggestionsError}</Alert>
                    <Button size="small" variant="outlined" onClick={() => refresh?.()}>Retry</Button>
                  </Box>
                ) : !suggestions?.length ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <LightbulbIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">No task suggestions</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {actionError && <Alert severity="error" sx={{ py: 0 }}>{actionError}</Alert>}
                    {suggestions.map((s) => (
                      <Paper
                        key={s.id}
                        elevation={0}
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                          {s.email_subject || 'No subject'}
                        </Typography>
                        {s.suggested_client_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {s.suggested_client_name}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          {(() => {
                            const conf = getConfidenceLabel(s.confidence_score ?? 0);
                            return conf ? (
                              <Chip label={conf.label} color={conf.color} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            ) : null;
                          })()}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={actioningId === s.id}
                            onClick={() => handleAcceptSuggestion(s.id)}
                            sx={{ textTransform: 'none', flex: 1 }}
                          >
                            {actioningId === s.id ? 'Creating...' : 'Create Task'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={actioningId === s.id}
                            onClick={() => handleDismissSuggestion(s.id)}
                            sx={{ textTransform: 'none' }}
                          >
                            Dismiss
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Collapse>
          {suggestionsPanelOpen === false && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', borderLeft: '1px solid', borderColor: 'divider', p: 0.5 }}>
              <IconButton size="small" onClick={() => setSuggestionsPanelOpen(true)} aria-label="Expand panel" title="Suggested Tasks">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              {(count ?? 0) > 0 && (
                <Chip label={count} size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }} />
              )}
            </Box>
          )}
        </Box>
    </PageLayout>
  );
};

export default InboxPage;
